import React from 'react';
import { useTranslation } from 'react-i18next';
import PanelFrame from '@components-clean/core/panel-frame';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { PanelTheme, PanelType } from 'types/graphql-global-types';
import { RadialChartObsActions } from '@utils/constants';
import { useGetRadialChartSiteData } from '@apis/bff-api/central-monitoring';

const ChartPanel = ({
    studyRef,
    selectedSiteId,
    datasetId,
}: {
    studyRef: string | undefined;
    selectedSiteId: number | undefined;
    datasetId: number | undefined;
}) => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.radialChart',
    });

    const { data: radialChartData, error, isLoading } = useGetRadialChartSiteData(studyRef, selectedSiteId, datasetId);

    const siteObservationsData = radialChartData?.siteObservations;
    const siteActionsData = radialChartData?.siteActions;
    const siteReviewLogsData = radialChartData?.siteReviewLogs;

    const panelDataSeries: { name: string; type: string; data: number[] }[] = [];
    const { legendToggleValue } = usePageToolbar();

    siteObservationsData?.forEach((siteObservation) => {
        if (siteObservation.count === 0) return;
        panelDataSeries.push({
            name: siteObservation.openClosed === 0 ? RadialChartObsActions.Open : RadialChartObsActions.Closed,
            type: 'column',
            data: [siteObservation.count, 0, 0],
        });
    });

    siteActionsData?.forEach((siteAction) => {
        if (siteAction.count === 0) return;
        panelDataSeries.push({
            name: siteAction.openClosed === 0 ? RadialChartObsActions.Open : RadialChartObsActions.Closed,
            type: 'column',
            data: [0, siteAction.count, 0],
        });
    });

    siteReviewLogsData?.forEach((siteReviewLog) => {
        if (siteReviewLog.count === 0) return;
        panelDataSeries.push({
            name: siteReviewLog.title,
            type: 'column',
            data: [0, 0, siteReviewLog.count],
        });
    });

    if (error) {
        return (
            <PanelFrame
                title={t('title')}
                data-testid="monitoring-status-card"
                className="monitoring-status-card"
                panelType={PanelType.GRAPH}
                graphType="RADIAL"
                panelData={{
                    series: [],
                    xCategories: [t('siteObservations'), t('siteActions'), t('siteReviewLogs')],
                }}
                hidePanelSetup
                theme={PanelTheme.THEME_2}
            />
        );
    }

    return (
        <PanelFrame
            title={t('title')}
            data-testid="monitoring-status-card"
            className="monitoring-status-card"
            panelType={PanelType.GRAPH}
            graphType="RADIAL"
            panelData={{
                plotTitle: t('title'),
                series: panelDataSeries,
                xCategories: [t('siteObservations'), t('siteActions'), t('siteReviewLogs')],
            }}
            hidePanelSetup
            theme={PanelTheme.THEME_2}
            isLoading={isLoading}
            showLegend={legendToggleValue?.current}
        />
    );
};

export default ChartPanel;
